import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleApiWrapper, Map } from "google-maps-react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { InputAdornment } from "@mui/material";
import { Map as MapIcon } from "lucide-react";

import { UISelect } from "../../../ui";
import messages from "../../../../assets/locale/messages";
import { fetchHeatmapRequest } from "../../../../store/HeatMap/actions";
import { getZoneOptionsFromEvent } from "../Applicants/eventRequestMappers";
import {
  CLUSTER_SPLIT_THRESHOLD,
  getClusterClickZoom,
  getClusterThresholdForZoom,
  groupNearbyMarkers,
} from "./heatmapHelpers";
import { MARKER_ICON_SIZE } from "./markerIcon";
import { useAvatarDataUrls } from "./useAvatarDataUrls";
import EventHeatMapMarkers from "./EventHeatMapMarkers";
import EventHeatMapControls from "./EventHeatMapControls";
import EventHeatMapClusterHint from "./EventHeatMapClusterHint";
import EventHeatMapTooltip from "./EventHeatMapTooltip";

import "./EventHeatMap.scss";

/** The tooltip renders above the marker by default; if the marker is closer
 * to the top of the map than this, there isn't room for the tooltip's
 * header to fit above it, so it flips to render below instead. */
const TOOLTIP_FLIP_THRESHOLD = 340;

/** Last-resort center for an event with no markers and no coordinates of
 * its own — without a valid center Google Maps renders a blank canvas. */
const DEFAULT_MAP_CENTER = { lat: 24.7136, lng: 46.6753 };

/** Where a marker's own coordinates land, in pixels within the map container.
 * The tooltip anchors to this instead of to the click point, so it always
 * hangs off the pin's edge no matter where inside the pin the user clicked. */
const getMarkerPixelPosition = (map, google, { lat, lng }) => {
  const projection = map?.getProjection?.();
  const bounds = map?.getBounds?.();
  if (!projection || !bounds) return null;

  const scale = 2 ** (map.getZoom() ?? 0);
  const marker = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
  const southWest = projection.fromLatLngToPoint(bounds.getSouthWest());
  const northEast = projection.fromLatLngToPoint(bounds.getNorthEast());

  return {
    x: (marker.x - southWest.x) * scale,
    y: (marker.y - northEast.y) * scale,
  };
};

const EventHeatMap = ({ google, event, portalVariant }) => {
  const eventId = event?.id;
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { data: markers, counts } = useSelector((state) => state.heatmapReducer);
  const t = messages[lang].events.viewEvents.heatmapTab;

  const isClientViewer = portalVariant === "client-portal";

  const [selectedZone, setSelectedZone] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [zoom, setZoom] = useState(14);
  const [openCluster, setOpenCluster] = useState(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    if (!eventId) return;
    dispatch(
      fetchHeatmapRequest({
        event_id: eventId,
        zone_id: selectedZone?.id,
        portalVariant,
      }),
    );
  }, [dispatch, eventId, selectedZone, portalVariant]);

  const zoneOptions = useMemo(() => getZoneOptionsFromEvent(event, lang), [event, lang]);

  const clusters = useMemo(
    () => groupNearbyMarkers(markers, getClusterThresholdForZoom(zoom)),
    [markers, zoom],
  );

  const avatarImageUrls = useMemo(
    () => [...new Set(markers.map((marker) => marker.user?.image).filter(Boolean))],
    [markers],
  );
  const avatarDataUrls = useAvatarDataUrls(avatarImageUrls);

  /** Centers on the newest marker (the API orders newest first); with no
   * markers on the map, falls back to the event's own coordinates. */
  const mapCenter = useMemo(() => {
    const first = markers[0];
    if (first) return { lat: first.lat, lng: first.lng };
    const lat = parseFloat(event?.lat);
    const lng = parseFloat(event?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : DEFAULT_MAP_CENTER;
  }, [markers, event]);

  const handleZoomIn = () => mapInstance?.setZoom((mapInstance.getZoom() ?? 12) + 1);
  const handleZoomOut = () => mapInstance?.setZoom((mapInstance.getZoom() ?? 12) - 1);
  const handleToggleLayer = () => {
    if (!mapInstance || !google) return;
    const nextType =
      mapInstance.getMapTypeId() === google.maps.MapTypeId.ROADMAP
        ? google.maps.MapTypeId.SATELLITE
        : google.maps.MapTypeId.ROADMAP;
    mapInstance.setMapTypeId(nextType);
  };
  const handleFullscreen = () => {
    const node = mapContainerRef.current;
    if (!node) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      node.requestFullscreen?.();
    }
  };

  /** Positions the popover like a chat bubble above the marker that was
   * clicked, using the click's pixel offset within the map container.
   * Clusters larger than the split threshold zoom the map in on click
   * instead of opening the tooltip, so they break down into smaller
   * sub-clusters — by real coordinates where those differ, and by an
   * evenly-chunked ring where the pins are stacked on one point.
   * Client viewers never get the tooltip (individual or clustered) at all. */
  const handleMarkerClick = (groupOrMember, markerKey) => () => {
    if (groupOrMember.members.length > CLUSTER_SPLIT_THRESHOLD) {
      mapInstance?.panTo({ lat: groupOrMember.lat, lng: groupOrMember.lng });
      mapInstance?.setZoom(getClusterClickZoom(groupOrMember, mapInstance.getZoom() ?? zoom));
      return;
    }

    if (isClientViewer) return;

    const pixel = getMarkerPixelPosition(mapInstance, google, groupOrMember);
    setOpenCluster({
      ...groupOrMember,
      key: markerKey,
      x: pixel?.x ?? null,
      y: pixel?.y ?? null,
    });
  };

  const tooltipPlacement =
    openCluster?.y != null && openCluster.y < TOOLTIP_FLIP_THRESHOLD ? "bottom" : "top";

  const tooltipPosition = openCluster?.x != null && openCluster?.y != null
    ? {
        left: openCluster.x,
        top:
          tooltipPlacement === "bottom"
            ? openCluster.y + MARKER_ICON_SIZE / 2
            : openCluster.y - MARKER_ICON_SIZE / 2,
      }
    : null;

  return (
    <div className="event-heatmap">
      <div className="event-heatmap__filter-row">
        <div className="event-heatmap__zone-select">
          <UISelect
            placeholder={t.allZones}
            options={zoneOptions}
            value={selectedZone}
            onChange={(value) => setSelectedZone(value)}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            textFieldProps={{
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MapIcon size={18} color="var(--color-foreground-subtle)" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>

        <div className="event-heatmap__indicators">
          <span className="event-heatmap__legend-item">
            <span className="event-heatmap__legend-dot event-heatmap__legend-dot--supervisor" />
            {t.supervisors}
            <span className="event-heatmap__legend-item-count">{counts.supervisors}</span>
          </span>
          <span className="event-heatmap__legend-item">
            <span className="event-heatmap__legend-dot event-heatmap__legend-dot--organizer" />
            {t.organizers}
            <span className="event-heatmap__legend-item-count">{counts.organizers}</span>
          </span>
          <span className="event-heatmap__legend-item">
            <span className="event-heatmap__legend-dot event-heatmap__legend-dot--offline" />
            {t.offline}
            <span className="event-heatmap__legend-item-count">{counts.offline}</span>
          </span>
        </div>
      </div>

      <div className="event-heatmap__map-container" ref={mapContainerRef}>
        <Map
          google={google}
          zoom={zoom}
          initialCenter={mapCenter}
          center={mapCenter}
          containerStyle={{ width: "100%", height: "100%", position: "relative" }}
          onReady={(_, map) => {
            setMapInstance(map);
            setZoom(map.getZoom());
            map.addListener("zoom_changed", () => {
              setZoom(map.getZoom());
              setOpenCluster(null);
            });
            map.addListener("dragstart", () => setOpenCluster(null));
          }}
        >
          <EventHeatMapMarkers
            google={google}
            clusters={clusters}
            zoom={zoom}
            avatarDataUrls={avatarDataUrls}
            onMarkerClick={handleMarkerClick}
          />
        </Map>

        <EventHeatMapControls
          t={t}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onToggleLayer={handleToggleLayer}
          onFullscreen={handleFullscreen}
        />

        <EventHeatMapClusterHint t={t} />

        {openCluster && (
          <EventHeatMapTooltip
            t={t}
            lang={lang}
            cluster={openCluster}
            position={tooltipPosition}
            placement={tooltipPlacement}
            isClientViewer={isClientViewer}
            onClose={() => setOpenCluster(null)}
          />
        )}
      </div>
    </div>
  );
};

EventHeatMap.propTypes = {
  google: PropTypes.object.isRequired,
  event: PropTypes.object,
  portalVariant: PropTypes.oneOf(["admin-portal", "company-portal", "client-portal"]),
};

EventHeatMap.defaultProps = {
  event: null,
  portalVariant: "admin-portal",
};

export default GoogleApiWrapper({
  apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
})(EventHeatMap);
